import { useCallback, useEffect, useState } from "react";
import type { SlotProps } from "../../../core/platform/registry/registry";
import { brand } from "../../../theme/brand.config";
import { activeTransport } from "../select-transport";
import { renderWifiVoucher } from "../wifi-voucher-layout";
import {
  claimWifiVoucher,
  countUnusedWifiVouchers,
  refreshWifiVoucherPool,
  LOW_STOCK_THRESHOLD,
  type WifiVoucherClaimResult,
} from "../wifi-voucher-module";
import { WifiVoucherLowStock } from "./WifiVoucherLowStock";

/**
 * The till-visible "WiFi code" action (T014, T016, T022 —
 * specs/003-wifi-voucher-tickets).
 *
 * REACHABLE WITHOUT A CART OR SALE (FR-301). Registered into
 * `terminal.actions`, the header bar's slot — sits alongside Sales/Menu/
 * Settings rather than inside the till's own toolbar, so it is reachable
 * from every screen the till can be in, not just the sale-taking one.
 *
 * A SINGLE PILL BUTTON, NOT A STACKED BLOCK. It shares `.terminal__signout`
 * with its header siblings so it never reads as a second, different kind of
 * control. Low stock is a small corner badge on the button itself (still
 * visible without a tap — FR-309's "persistent" requirement — but costs no
 * extra row), and a failed/empty claim is a dismissible floating note rather
 * than inline text, so a long failure message never pushes header buttons
 * around or wraps the bar onto a second line by itself.
 *
 * REUSES THE DRAWER/TICKET `FailureCode` VOCABULARY (contract) — a voucher
 * fails to print for the same hardware reasons a ticket does, and the
 * exhausted-pool message ("empty") is deliberately its own branch, distinct
 * from every hardware `FailureCode` (FR-310).
 */

const CAUSE_TEXT: Record<string, string> = {
  permission_lost: "The printer needs to be reconnected in settings.",
  device_disconnected: "The printer isn't responding. Check the cable or that it's on.",
  unsupported_browser: "This device can't print WiFi codes.",
  kick_timeout: "The code didn't print.",
};

function resultMessage(result: WifiVoucherClaimResult): string | null {
  switch (result.outcome) {
    case "issued":
      return null;
    case "empty":
      return "No WiFi codes remain — load a new pool.";
    case "failed":
      return CAUSE_TEXT[result.failureCode] ?? "The code didn't print.";
  }
}

export function WifiVoucherButton({ staffId, terminalId, token }: SlotProps) {
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const refreshCount = useCallback(async () => {
    setCount(await countUnusedWifiVouchers());
  }, []);

  useEffect(() => {
    if (typeof token !== "string") return;
    void refreshWifiVoucherPool(token)
      .catch(() => undefined)
      .then(refreshCount);
  }, [token, refreshCount]);

  async function handleClaim() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await claimWifiVoucher({
        transport: activeTransport,
        render: (code) => renderWifiVoucher(code, brand.wifiVoucher),
        terminalId: () => (typeof terminalId === "string" ? terminalId : null),
        staffId: () => (typeof staffId === "string" ? staffId : ""),
      });
      setMessage(resultMessage(result));
      await refreshCount();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="terminal__signout wifivoucher__button"
        onClick={() => void handleClaim()}
        disabled={busy}
      >
        {busy ? "Printing…" : "WiFi code"}
        {count !== null && <WifiVoucherLowStock count={count} threshold={LOW_STOCK_THRESHOLD} />}
      </button>

      {message && (
        <div className="wifivoucher__toast" role="alert">
          <span>{message}</span>
          <button
            type="button"
            className="wifivoucher__dismiss"
            onClick={() => setMessage(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}

export default WifiVoucherButton;
